import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { closeDispute } from './api';
import { useInvoicesBase } from './basePath';

const OUTCOMES = [
  ['accepted_as_billed', 'Accepted as billed'],
  ['short_paid', 'Lines corrected to what we will pay'],
  ['withdrawn', 'We were wrong, no dispute'],
];

/**
 * An open dispute with the supplier (backend/invoice_dispute.py): the invoice cannot be approved until a person closes it.
 * Points at the claim (reviewed and sent from Supplier claims) and closes the dispute with how it ended and why.
 */
export default function DisputeBanner({ invoice, canDecide = true, onChanged }) {
  const dispute = invoice.dispute;
  const base = useInvoicesBase();
  const [closing, setClosing] = useState(false);
  const [outcome, setOutcome] = useState(OUTCOMES[0][0]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!dispute || dispute.state !== 'open') return null;

  const submit = async () => {
    setBusy(true); setError(null);
    try { await closeDispute(invoice.id, outcome, reason.trim()); onChanged?.(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-amber-400 bg-amber-100 p-5 space-y-2" data-testid="dispute-banner">
      <h2 className="text-lg font-semibold text-gray-900">Disputed with the supplier</h2>
      <p className="text-sm text-gray-800" data-testid="dispute-banner-text">
        Reference <strong>{dispute.reference}</strong>{dispute.amount ? `, £${dispute.amount} in question` : ''}.
        This invoice cannot be approved until the dispute is closed.{' '}
        <Link to={`/receiving/claims/${dispute.claim_id}`} className="underline" data-testid="dispute-banner-claim">
          Review and send the claim
        </Link>
      </p>
      {dispute.reply_invoice_id && (
        <p className="text-sm text-gray-800" data-testid="dispute-banner-reply">
          The supplier has answered with a new invoice.{' '}
          <Link to={`${base}/${dispute.reply_invoice_id}`} className="underline" data-testid="dispute-banner-reply-link">Open it</Link>
          {' '}and choose Replace if it corrects this one.
        </p>
      )}
      {dispute.credit_received && (
        <p className="text-sm text-gray-800" data-testid="dispute-banner-credit">
          A credit note for £{dispute.credit_received} was matched to the claim
          {dispute.credit_outstanding ? `; £${dispute.credit_outstanding} is still owed` : ''}.
        </p>
      )}
      {canDecide && !closing && (
        <button type="button" onClick={() => setClosing(true)} data-testid="dispute-close-open" className="underline text-sm">
          Close the dispute
        </button>
      )}
      {canDecide && closing && (
        <div className="space-y-2" data-testid="dispute-close">
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="dispute-close-outcome"
            className="rounded-lg border border-gray-300 p-2 text-sm">
            {OUTCOMES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500}
            aria-label="Why is the dispute closed?" data-testid="dispute-close-reason"
            className="w-full rounded-lg border border-gray-300 p-2 text-sm" />
          <div className="flex gap-2">
            <button type="button" disabled={busy || !reason.trim()} onClick={submit} data-testid="dispute-close-submit"
              className="min-h-[44px] px-4 rounded-xl bg-amber-600 text-white disabled:opacity-50">Close the dispute</button>
            <button type="button" onClick={() => setClosing(false)} className="min-h-[44px] px-4 rounded-xl text-gray-700">Cancel</button>
          </div>
          {error && <p className="text-sm text-red-700" data-testid="dispute-close-error">{error}</p>}
        </div>
      )}
    </section>
  );
}
